// src/services/portalService.js
import api from "./api";

// Monta querystring a partir de pares idCondominio/idUnidade (e quaisquer outros campos).
// Ignora valores vazios/undefined/null para manter URLs limpas.
const qs = (params = {}) => {
  const entries = Object.entries(params).filter(([, v]) => v !== "" && v !== undefined && v !== null);
  if (!entries.length) return "";
  return "?" + new URLSearchParams(entries).toString();
};

const portalService = {
  minhasUnidades:        () => api("GET", "/portal/minhas-unidades"),

  // Endpoints que filtram por (condomínio, unidade)
  alertas:               (idCondominio, idUnidade) => api("GET", `/portal/alertas${qs({ idCondominio, idUnidade })}`),
  boletos:               (idCondominio, idUnidade) => api("GET", `/portal/boletos${qs({ idCondominio, idUnidade })}`),
  reservas:              (idCondominio, idUnidade) => api("GET", `/portal/reservas${qs({ idCondominio, idUnidade })}`),
  ocorrencias:           (idCondominio, idUnidade) => api("GET", `/portal/ocorrencias${qs({ idCondominio, idUnidade })}`),
  encomendas:            (idCondominio, idUnidade) => api("GET", `/portal/encomendas${qs({ idCondominio, idUnidade })}`),
  moradores:             (idCondominio, idUnidade) => api("GET", `/portal/moradores${qs({ idCondominio, idUnidade })}`),
  veiculos:              (idCondominio, idUnidade) => api("GET", `/portal/veiculos${qs({ idCondominio, idUnidade })}`),
  pets:                  (idCondominio, idUnidade) => api("GET", `/portal/pets${qs({ idCondominio, idUnidade })}`),
  historicoVisitas:      (p = {}) => api("GET", `/portal/historico-visitas${qs(p)}`),

  // Endpoints que filtram apenas por condomínio (não há recorte por unidade)
  comunicados:           (idCondominio, idUnidade) => api("GET", `/portal/comunicados${qs({ idCondominio, idUnidade })}`),
  assembleias:           (idCondominio, idUnidade) => api("GET", `/portal/assembleias${qs({ idCondominio, idUnidade })}`),
  prestacoes:            (idCondominio) => api("GET", `/portal/prestacao-contas${qs({ idCondominio })}`),
  demonstrativos:        (idCondominio) => api("GET", `/portal/demonstrativos${qs({ idCondominio })}`),
  prestacaoFechamento:   (idCondominio, competencia) => api("GET", `/portal/prestacao-fechamento${qs({ idCondominio, competencia })}`),

  // Áreas comuns / calendário / reservas (criar/cancelar)
  areasComuns:           (idCondominio) => api("GET", `/portal/areas-comuns${qs({ idCondominio })}`),
  calendarioArea:        (id, mes) => api("GET", `/portal/areas-comuns/${id}/calendario?mes=${mes}`),
  criarReserva:          (data) => api("POST", "/portal/reservas", data),
  cancelarReserva:       (id) => api("PATCH", `/portal/reservas/${id}/cancelar`),

  // Comunicados
  confirmarLeitura:      (id, data={}) => api("POST", `/portal/comunicados/${id}/leitura`, data),

  // Assembleias
  assembleiaResultados:  (id) => api("GET", `/portal/assembleias/${id}/resultados`),
  confirmarPresenca:     (id, idUnidade) => api("POST", `/portal/assembleias/${id}/presenca`, { idUnidade }),
  verAta:                (id) => api("GET",  `/portal/assembleias/${id}/ata`),
  assembleiaPautas:      (id) => api("GET", `/portal/assembleias/${id}/pautas`),
  votar:                 (id, data) => api("POST", `/portal/assembleias/${id}/votar`, data),

  // Ocorrências
  criarOcorrencia:       (data) => api("POST", "/portal/ocorrencias", data),
  responderOcorrencia:   (id, comentario) => api("POST", `/portal/ocorrencias/${id}/responder`, { comentario }),
  downloadOcorrenciaAnexo: (idOcorrencia, idAnexo) =>
    api("GET", `/portal/ocorrencias/${idOcorrencia}/anexos/${idAnexo}`),

  // Moradores
  criarMorador:          (data) => api("POST", "/portal/moradores", data),
  atualizarMorador:      (id, data) => api("PUT", `/portal/moradores/${id}`, data),
  removerMorador:        (id) => api("DELETE", `/portal/moradores/${id}`),

  // Veículos
  criarVeiculo:          (data) => api("POST",   "/portal/veiculos", data),
  atualizarVeiculo:      (id, data) => api("PUT", `/portal/veiculos/${id}`, data),
  removerVeiculo:        (id) => api("DELETE", `/portal/veiculos/${id}`),

  // Pets
  criarPet:              (data) => api("POST",   "/portal/pets", data),
  atualizarPet:          (id, data) => api("PUT", `/portal/pets/${id}`, data),
  removerPet:            (id) => api("DELETE", `/portal/pets/${id}`),

  // Prestação de contas / demonstrativos detalhe
  prestacaoDetalhe:      (id) => api("GET", `/portal/prestacao-contas/${id}`),
  demonstrativo:         (id) => api("GET", `/portal/demonstrativos/${id}`),

  // ── Perfil do condômino (avatar, senha, preferências) ───────────────────
  buscarPerfil:          () => api("GET", "/portal/perfil"),
  atualizarPerfil:       (data) => api("PUT", "/portal/perfil", data),
  atualizarAvatar:       (avatarBase64) => api("PUT", "/portal/perfil/avatar", { avatar: avatarBase64 }),
  removerAvatar:         () => api("DELETE", "/portal/perfil/avatar"),
  trocarSenha:           (senhaAtual, novaSenha) => api("POST", "/portal/perfil/senha", { senhaAtual, novaSenha }),
  iniciarMudancaContato: (tpCanal, novoValor) => api("POST", "/portal/perfil/contato/iniciar", { tpCanal, novoValor }),
  confirmarMudancaContato:(tpCanal, codigo)   => api("POST", "/portal/perfil/contato/confirmar", { tpCanal, codigo }),
  listarSessoes:         () => api("GET", "/portal/perfil/sessoes"),
  revogarSessao:         (id) => api("DELETE", `/portal/perfil/sessoes/${id}`),
  revogarOutrasSessoes:  () => api("DELETE", "/portal/perfil/sessoes/outras"),
  logoutTodasSessoes:    () => api("POST", "/portal/perfil/logout-todas-sessoes"),

  // Portaria (condômino → portaria / prestadores)
  avisosPortaria:        (idCondominio, idUnidade) =>
    api("GET", `/portal/avisos-portaria${qs({ idCondominio, idUnidade })}`),
  criarAvisoPortaria:    (data) => api("POST", "/portal/avisos-portaria", data),
  responderAvisoPortaria:(id, mensagem) =>
    api("POST", `/portal/avisos-portaria/${id}/mensagem`, { mensagem }),
  prestadoresPortal:     (idCondominio, search = "") =>
    api("GET", `/portal/prestadores${qs({ idCondominio, search })}`),
  autorizarPrestador:    (idPrestador, data) =>
    api("POST", `/portal/prestadores/${idPrestador}/autorizar`, data),
};

export default portalService;
